/**
 * A porta entre a tela de operação e a API do módulo 03.
 *
 * É o único arquivo do módulo que conhece `fetch`, caminho de rota e formato de
 * JSON. Quem está acima dele — `consultas.ts` e a tela — fala em domínio.
 *
 * ## A queda de erro é por SITUAÇÃO, e não uma frase só
 *
 * Mesma decisão dos módulos 08 e 12, pelo mesmo motivo medido lá: com o
 * servidor rodando uma versão anterior, o `GET` responde 404 e uma frase única
 * diria "confira os campos" numa lista que não tem campo nenhum. Mandar
 * procurar o erro onde ele não está é pior do que não dizer nada.
 *
 * O `403` ganhou frase própria nesta rodada, e ela vem do SERVIDOR: o portão de
 * autorização nomeia a permissão que falta, e repetir "sem acesso" aqui
 * apagaria justamente o que quem administra precisa ler para liberar.
 */
import { buscar } from '@/lib/licenca-das-rotas';
import type { SaidaParaAObra } from './saida';
import { BASE_DA_API, CREDENCIAIS_DA_API } from '@/lib/api';

const BASE = BASE_DA_API;

/** Não há cookie de sessão válido: ninguém entrou, ou o prazo expirou. */
export class SemSessao extends Error {
  constructor() {
    super('não há sessão aberta');
    this.name = 'SemSessao';
  }
}

/** O backend não respondeu. Rede, porta fechada, processo fora do ar. */
export class ApiIndisponivel extends Error {
  constructor() {
    super('o sistema não respondeu');
    this.name = 'ApiIndisponivel';
  }
}

/** O pedido chegou e foi recusado — com o que fazer em seguida. */
export class RecusaDaApi extends Error {
  readonly comoResolver: string;
  readonly status: number;

  constructor(erro: string, comoResolver: string, status: number) {
    super(erro);
    this.name = 'RecusaDaApi';
    this.comoResolver = comoResolver;
    this.status = status;
  }
}

interface CorpoDeErro {
  readonly erro?: string;
  readonly comoResolver?: string;
}

function quedaDoErro(status: number): { erro: string; comoResolver: string } {
  if (status === 403) {
    return {
      erro: 'você não tem permissão para isto',
      comoResolver: 'Peça a quem administra o sistema para liberar esta ação para o seu perfil.',
    };
  }
  if (status === 404) {
    return {
      erro: 'o servidor não conhece este endereço',
      comoResolver:
        'Recarregue a página. Se o sistema acabou de ser atualizado, o servidor ainda está com a versão anterior e precisa ser reiniciado.',
    };
  }
  if (status === 409) {
    return {
      erro: 'este registro já foi tratado por outra pessoa',
      comoResolver: 'Recarregue a lista para ver quem tratou e quando.',
    };
  }
  if (status >= 500) {
    return {
      erro: 'o servidor falhou ao responder',
      comoResolver: 'Tente de novo; se continuar, avise o suporte com o horário em que aconteceu.',
    };
  }
  return {
    erro: 'o pedido não foi aceito',
    comoResolver: 'Confira o que foi preenchido e tente outra vez; se continuar, avise o suporte com o horário.',
  };
}

async function pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
  let resposta: Response;
  try {
    /* A leitura sem a licenca da rota nem sai: ver `@/lib/licenca-das-rotas`. */
    resposta = await buscar(`${BASE}${caminho}`, caminho, {
      credentials: CREDENCIAIS_DA_API,
      headers: init?.body ? { 'content-type': 'application/json' } : {},
      ...init,
    });
  } catch {
    throw new ApiIndisponivel();
  }

  if (resposta.status === 401) throw new SemSessao();

  if (!resposta.ok) {
    const corpo = (await resposta.json().catch(() => ({}))) as CorpoDeErro;
    throw new RecusaDaApi(
      corpo.erro ?? quedaDoErro(resposta.status).erro,
      corpo.comoResolver ?? quedaDoErro(resposta.status).comoResolver,
      resposta.status,
    );
  }

  return (await resposta.json()) as T;
}

const enviar = <T,>(caminho: string, corpo: unknown): Promise<T> =>
  pedir<T>(caminho, { method: 'POST', body: JSON.stringify(corpo) });

const alterar = <T,>(caminho: string, corpo: unknown): Promise<T> =>
  pedir<T>(caminho, { method: 'PATCH', body: JSON.stringify(corpo) });

/* ==========================================================================
 * As linhas, exatamente como a API as devolve
 * ======================================================================= */

/**
 * Um turno fechado.
 *
 * `horasMotor` e `horasApontadas` são MEDIDAS DIFERENTES e não somam. O motor
 * anda durante produção, espera e deslocamento — uma escavadeira esperando
 * caminhão está com o motor ligado e em espera ao mesmo tempo. As quatro
 * particionam o turno; o motor mede a máquina. Ver o cabeçalho da migração
 * 0014, e a tela diz isso em texto para quem não lê migração.
 */
export interface ApontamentoDaApi {
  readonly id: string;
  readonly dia: string;
  readonly turno: string;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly equipamentoDescricao: string;
  readonly operadorId: string | null;
  readonly operador: string | null;
  readonly atividade: string | null;
  readonly observacao: string | null;
  /** As leituras do medidor, na unidade de `tipoMedidor`: h no horímetro, km no odômetro (0113). */
  readonly horimetroInicial: string;
  readonly horimetroFinal: string;
  /**
   * Decisão 51 (0113): o medidor da máquina no lançamento. Ausente em servidor
   * anterior à 0113 — aí é horímetro, porque o caminhão era recusado.
   */
  readonly tipoMedidor?: 'horimetro' | 'odometro';
  /** Nula no odômetro: km não é hora (0113). */
  readonly horasMotor: string | null;
  readonly horasProducao: string;
  readonly horasEspera: string;
  readonly horasDeslocamento: string;
  readonly horasManutencao: string;
  readonly horasApontadas: string;
  readonly conferidoEm: string | null;
  readonly conferidoPor: string | null;
  readonly conferidoPorNome: string | null;
  readonly criadoEm: string;
  /**
   * Como o turno se dividiu, na ordem do dia (0070) — so o turno apontado pelo
   * celular tem. Ausente quando o servidor ainda e anterior a 0070.
   */
  readonly intervalos?: readonly IntervaloDaApi[];
  /**
   * 0111 · como o operador se identificou no campo, sem servidor: tocando no
   * nome da lista do pacote, ou pelo crachá. **Nulo no apontamento da mesa e
   * da folha**, que têm sessão de verdade e não declaram nada — e é essa a
   * diferença que a conferência precisa enxergar.
   */
  readonly identificacaoDoOperador?: 'toque' | 'cracha' | null;
  /** A causa da hora parada (0086), do catálogo. */
  readonly causaId?: string | null;
  readonly causaCodigo?: string | null;
  readonly causa?: string | null;
  /** O turno estornado que este corrige, e o motivo da correção (ADR-008, 0086). */
  readonly corrigeId?: string | null;
  readonly motivoDaCorrecao?: string | null;
  /**
   * R06 (p.69), 0094: os valores do turno que esta correção substituiu, para
   * a segunda linha "Correção: horímetro final 4.290 → 4.296". Nulo fora de
   * correção; ausente em servidor anterior à 0094.
   */
  readonly antesDaCorrecao?: AntesDaCorrecaoDaApi | null;
  /** O turno que esta correção substituiu, resumido como o celular lê (M52). */
  readonly anterior?: { readonly apontadoPorNome?: string | null; readonly criadoEm?: string | null; readonly horimetroFinal?: string | null } | null;
  /** Quem apontou este turno (R06). */
  readonly apontadoPorNome?: string | null;
}

export interface AntesDaCorrecaoDaApi {
  readonly dia: string;
  readonly turno: string;
  readonly horimetroInicial: number | null;
  readonly horimetroFinal: number | null;
  readonly horasApontadas: number | null;
  readonly horasEspera: number | null;
  readonly causaCodigo: string | null;
  /** Quem apontou o turno que a correção substituiu, e quando. */
  readonly apontadoPorNome?: string | null;
  readonly apontadoEm?: string | null;
}

/**
 * O turno estornado (R06, p.69; 0094), para a chave "Mostrar estornados".
 * Para mostrar, nunca para somar: a soma do período é a dos vigentes.
 */
export interface TurnoEstornadoDaApi {
  readonly id: string;
  readonly dia: string;
  readonly turno: string;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly equipamentoDescricao: string;
  readonly operador: string | null;
  readonly horimetroInicial: number | null;
  readonly horimetroFinal: number | null;
  /** A unidade das leituras, pelo medidor da máquina (0113). */
  readonly tipoMedidor?: 'horimetro' | 'odometro' | null;
  readonly horasApontadas: number | null;
  readonly horasEspera: number | null;
  readonly causaCodigo: string | null;
  readonly causa: string | null;
  readonly estornadoEm: string;
  readonly motivo: string | null;
  readonly estornadoPor: string | null;
  /** O turno que corrigiu este, quando o estorno foi uma correção. */
  readonly corrigidoPorId: string | null;
  /** O mesmo `motivo`, com o nome do abastecimento. */
  readonly motivoDoEstorno?: string | null;
  readonly apontadoPorNome?: string | null;
  readonly apontadoEm?: string | null;
}

export interface IntervaloDaApi {
  readonly natureza: 'producao' | 'espera' | 'deslocamento' | 'manutencao';
  readonly inicio: string;
  readonly fim: string;
  readonly horas: number;
  readonly atividade: string | null;
  /**
   * 0111 · o intervalo fechou na **última batida** do aparelho, e não num
   * toque: ele morreu com a atividade correndo. "Produziu das 07:00 às 11:00"
   * é um fato apontado; isto é um fato apontado **mais uma dúvida** sobre o
   * que veio depois — e quem confere precisa ver a diferença.
   *
   * Opcional porque o servidor anterior à 0111 não devolve o campo.
   */
  readonly fechadoPelaBatida?: boolean;
}

export interface HorasDaApi {
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly turnos: string;
  /** Nula quando a máquina só tem turnos de odômetro (0113): km não é hora. */
  readonly horasMotor: string | null;
  readonly horasProducao: string;
  readonly horasEspera: string;
  readonly horasDeslocamento: string;
  readonly horasManutencao: string;
  readonly horasApontadas: string;
  readonly turnosNaoConferidos: string;
  /** A janela programada do período pelo turno da empresa (0027). Nulo sem turno cadastrado. */
  readonly horasProgramadas?: number | null;
  readonly disponibilidade?: number | null;
  readonly aproveitamento?: number | null;
  readonly horasSemApontamento?: number | null;
}

export interface JanelaDaApi {
  readonly cadastrada: boolean;
  readonly de: string | null;
  readonly ate: string | null;
  readonly horasProgramadas: number | null;
}

/** Um turno da empresa (decisão 19). `diaSemana` 0 é domingo. */
/**
 * Os intervalos apontados como a consulta das leituras os leva:
 * `07:00-07:30,13:00-14:00`. Sem eles, o servidor mede o turno pelo turno
 * cadastrado (17:00 do DIURNO), e a gravacao pelos intervalos (07:30): o
 * inicial sugerido saia de uma leitura posterior ao turno, e o banco recusava
 * (robos no QAS, rodada 3).
 */
export function intervalosDaConsulta(intervalos: readonly { readonly inicio: string; readonly fim: string }[]): string {
  return intervalos.map((i) => `${i.inicio}-${i.fim}`).join(',');
}

export function consultaDasLeiturasDoTurno(equipamentoId: string, dia: string, turno: string, intervalos: string): string {
  return `/operacao/leituras-do-turno?equipamento=${encodeURIComponent(equipamentoId)}&dia=${encodeURIComponent(dia)}&turno=${encodeURIComponent(turno)}${intervalos ? `&intervalos=${encodeURIComponent(intervalos)}` : ''}`;
}

/** As leituras do medidor em volta do turno: `lidaEm` em ISO. */
export interface LeiturasDoTurno {
  readonly antes: { readonly valor: number; readonly lidaEm: string } | null;
  readonly depois: { readonly valor: number; readonly lidaEm: string } | null;
  /** Quem está logado tem aptidão vigente para operar esta máquina. */
  readonly logadoApto: boolean;
}

export interface TurnoDaApi {
  readonly id?: string;
  readonly nome: string;
  readonly diaSemana: number;
  readonly inicio: string;
  readonly fim: string;
  readonly intervaloMin: number;
  readonly horas?: number;
}

export interface ParadaDaApi {
  readonly motivoId: string;
  readonly motivoCodigo: string;
  readonly motivo: string;
  readonly evitavel: boolean;
  readonly ocorrencias: string;
  /** Nulo quando TODAS as paradas dessa causa ainda estão abertas. */
  readonly horasParadas: string | null;
  readonly emAberto: string;
}

export interface CadastrosDaApi {
  /** `tipoMedidor` (0086): diz a unidade das leituras do turno — h no horímetro, km no odômetro (0113). */
  readonly equipamentos: readonly {
    id: string;
    codigo: string;
    descricao: string;
    tipoMedidor?: 'horimetro' | 'odometro' | null;
    /** O código interno e a placa, nas etiquetas do seletor da máquina (31/10/2026). */
    codigoInterno?: string | null;
    placa?: string | null;
  }[];
  readonly operadores: readonly { id: string; nome: string }[];
  readonly motivos: readonly { id: string; codigo: string; nome: string; evitavel: boolean }[];
}

/** Uma causa do catálogo, como o cadastro genérico a devolve. */
export interface MotivoDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly evitavel: boolean;
  readonly ativo: boolean;
  /** A versao da linha (backend/src/http/versao-da-ficha.ts). */
  readonly versao?: string | null;
}

export interface EnvioDeMotivo {
  readonly codigo: string;
  readonly nome: string;
  readonly evitavel: boolean;
  readonly ativo?: boolean;
  readonly motivo?: string;
  /** A versao de quando o formulario abriu: o servidor recusa se outra pessoa gravou depois. */
  readonly versao?: string;
}

export interface EnvioDeApontamento {
  readonly equipamentoId: string;
  readonly dia: string;
  readonly turno: string;
  readonly operadorId?: string | null;
  readonly atividade?: string | null;
  readonly observacao?: string | null;
  readonly horimetroInicial: number;
  readonly horimetroFinal: number;
  readonly horasProducao: number;
  readonly horasEspera: number;
  readonly horasDeslocamento: number;
  readonly horasManutencao: number;
  /** A causa da hora parada (0086): só com espera ou manutenção. */
  readonly causaId?: string | null;
  /** A hora do turno (0121), hh:mm: os dois juntos. O fim é quando o horímetro final foi lido. */
  readonly inicio?: string;
  readonly fim?: string;
}

/* ==========================================================================
 * OPE 03 — importação com área de estágio e a fila de divergências
 * ======================================================================= */

export interface LoteDaApi {
  readonly id: string;
  readonly origem: string;
  readonly nomeArquivo: string;
  readonly situacao: 'estagio' | 'promovido' | 'descartado';
  readonly periodoInicio: string | null;
  readonly periodoFim: string | null;
  readonly linhas: number;
  readonly linhasValidas: number;
  readonly motivoDescarte: string | null;
  readonly criadoEm: string;
  readonly criadoPor: string | null;
  readonly promovidoEm: string | null;
  readonly promovidoPor: string | null;
}

export interface LinhaImportadaDaApi {
  readonly numeroLinha: number;
  readonly bruto: string;
  readonly codigoEquipamento: string | null;
  readonly equipamento: string | null;
  readonly dia: string | null;
  readonly horas: number | null;
  readonly erro: string | null;
}

export interface DivergenciaDaApi {
  readonly id: string;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly dia: string;
  readonly tipo: string;
  readonly horasMotor: number | null;
  readonly horasTurno: number | null;
  readonly horasProducao: number | null;
  readonly horasReconhecidas: number | null;
  /** As horas de motor da telemetria no dia (0103); nulo nas divergências que não são dela. */
  readonly horasTelemetria?: number | null;
  readonly diferencaH: number;
  readonly situacao: 'aberta' | 'resolvida';
  readonly responsavelId: string | null;
  readonly responsavel: string | null;
  readonly evidencia: string | null;
  readonly justificativa: string | null;
  readonly resolucao: string | null;
  readonly resolvidaPor: string | null;
  readonly resolvidaEm: string | null;
  readonly apuradaEm: string;
}

export interface FiltroDaFila {
  readonly de?: string;
  readonly ate?: string;
  readonly situacao?: 'aberta' | 'resolvida';
}

export interface EnvioDeResolucao {
  readonly resolucao: string;
  readonly justificativa: string;
  readonly evidencia?: string | null;
}

/** O filtro da tela. Vazio quer dizer "tudo", e não "nada". */
export interface Filtro {
  readonly de?: string;
  readonly ate?: string;
  readonly equipamentoId?: string;
}

function comFiltro(caminho: string, filtro: Filtro): string {
  const busca = new URLSearchParams();
  if (filtro.de) busca.set('de', filtro.de);
  if (filtro.ate) busca.set('ate', filtro.ate);
  if (filtro.equipamentoId) busca.set('equipamentoId', filtro.equipamentoId);
  const texto = busca.toString();
  return texto ? `${caminho}?${texto}` : caminho;
}

export const api = {
  cadastros: () => pedir<CadastrosDaApi>('/operacao/cadastros'),

  motivos: () => pedir<{ itens: readonly MotivoDaApi[] }>('/operacao/motivos-de-parada'),

  /**
   * Criar e alterar pela MESMA função, e o `id` é que decide.
   *
   * O cadastro genérico do backend não tem `DELETE` por decisão registrada
   * lá: causa de parada que já foi usada não pode sumir, senão o histórico
   * perde o agrupamento. Arquivar é `ativo: false`, e é um `PATCH`.
   */
  gravarMotivo: (id: string | null, envio: EnvioDeMotivo) =>
    id === null
      ? enviar<MotivoDaApi>('/operacao/motivos-de-parada', envio)
      : alterar<MotivoDaApi>(`/operacao/motivos-de-parada/${id}`, envio),

  apontamentos: (filtro: Filtro) =>
    pedir<{ apontamentos: readonly ApontamentoDaApi[] }>(comFiltro('/operacao/apontamentos', filtro)),

  /** Os turnos estornados do mesmo recorte (R06, p.69; 0094). */
  turnosEstornados: async (filtro: Filtro) => {
    const caminho = comFiltro('/operacao/apontamentos', filtro);
    const r = await pedir<{ estornados?: readonly TurnoEstornadoDaApi[] }>(`${caminho}${caminho.includes('?') ? '&' : '?'}comEstornados=1`);
    return r.estornados ?? [];
  },

  horas: (filtro: Filtro) =>
    pedir<{ horas: readonly HorasDaApi[]; janela?: JanelaDaApi }>(comFiltro('/operacao/horas', filtro)),

  /** O que saiu para a obra nos ultimos dias, e se o apontamento chegou (0112). */
  saidas: () => pedir<{ saidas: readonly SaidaParaAObra[] }>('/operacao/saidas-para-a-obra'),

  turnos: () => pedir<{ turnos: readonly TurnoDaApi[] }>('/operacao/turnos'),
  /** A última leitura antes do fim do turno e a primeira depois dele (achado dos robôs, 20/10/2026). */
  leiturasDoTurno: (equipamentoId: string, dia: string, turno: string, intervalos = '') =>
    pedir<LeiturasDoTurno>(consultaDasLeiturasDoTurno(equipamentoId, dia, turno, intervalos)),
  gravarTurnos: (turnos: readonly TurnoDaApi[], motivo: string) =>
    pedir<{ turnos: readonly TurnoDaApi[] }>('/operacao/turnos', {
      method: 'PUT',
      body: JSON.stringify({
        turnos: turnos.map((t) => ({ nome: t.nome, diaSemana: t.diaSemana, inicio: t.inicio, fim: t.fim, intervaloMin: t.intervaloMin })),
        motivo,
      }),
    }),

  paradas: (filtro: Filtro) =>
    pedir<{ paradas: readonly ParadaDaApi[] }>(comFiltro('/operacao/paradas-por-motivo', filtro)),

  apontar: (envio: EnvioDeApontamento) =>
    enviar<{ apontamentoId: string }>('/operacao/apontamentos', envio),

  /**
   * Conferir tem porta PRÓPRIA, e não é um campo do apontamento.
   *
   * Ela exige outra permissão — `apontamento.conferir`, que o v6 atribui ao
   * encarregado —, e permissão diferente não cabe no mesmo endereço. E é um
   * ATO: "este registro fecha". Estados que mudam o que o sistema afirma
   * merecem um gesto deliberado, e não um campo editado de passagem.
   */
  conferir: (id: string, motivo?: string) =>
    enviar<{ conferido: boolean }>(
      `/operacao/apontamentos/${id}/conferencia`,
      motivo === undefined ? {} : { motivo },
    ),

  /**
   * ESTORNAR E CORRIGIR o turno (ADR-008, 0086), com licença própria
   * (`apontamento_hora.estornar`) e motivo de pelo menos 15 caracteres. O
   * estornado sai de toda soma, com a leitura de horímetro que ele gravou;
   * corrigir é estornar e lançar o certo, juntos.
   */
  estornar: (id: string, motivo: string) =>
    enviar<{ estornado: boolean; apontamentoId: string }>(`/operacao/apontamentos/${id}/estorno`, { motivo }),
  corrigir: (id: string, motivo: string, apontamento: EnvioDeApontamento) =>
    enviar<{ estornadoId: string; apontamentoId: string }>(`/operacao/apontamentos/${id}/correcao`, { motivo, apontamento }),

  /* ---------------------------------------------------------------- OPE 03 */
  importacoes: () => pedir<{ lotes: readonly LoteDaApi[]; total: number; limite: number }>('/operacao/importacoes'),
  importacao: (id: string) =>
    pedir<{ lote: LoteDaApi; linhas: readonly LinhaImportadaDaApi[] }>(`/operacao/importacoes/${id}`),
  /** O arquivo vai como TEXTO: o servidor calcula o hash sobre o que chegou. */
  importar: (nomeArquivo: string, conteudo: string) =>
    enviar<{ loteId: string; linhas: number; linhasValidas: number }>('/operacao/importacoes', { nomeArquivo, conteudo }),
  promover: (id: string) =>
    enviar<{ promovidas: number; periodo: { de: string; ate: string }; divergenciasAbertas: number }>(
      `/operacao/importacoes/${id}/promocao`,
      {},
    ),
  descartar: (id: string, motivo: string) =>
    enviar<{ descartado: boolean }>(`/operacao/importacoes/${id}/descarte`, { motivo }),

  divergencias: (filtro: FiltroDaFila) => {
    const busca = new URLSearchParams();
    if (filtro.de) busca.set('de', filtro.de);
    if (filtro.ate) busca.set('ate', filtro.ate);
    if (filtro.situacao) busca.set('situacao', filtro.situacao);
    const texto = busca.toString();
    return pedir<{ divergencias: readonly DivergenciaDaApi[]; total: number; limite: number }>(
      texto ? `/operacao/divergencias?${texto}` : '/operacao/divergencias',
    );
  },
  apurar: (de: string, ate: string) =>
    enviar<{ divergenciasAbertas: number }>('/operacao/divergencias/apuracao', { de, ate }),
  resolver: (id: string, envio: EnvioDeResolucao) =>
    enviar<{ resolvida: boolean }>(`/operacao/divergencias/${id}/resolucao`, envio),

  /**
   * Atribuir o responsavel pela divergencia.
   *
   * A rota existe desde a OPE 03 (`0037`) e a tela nao a alcancava: o resumo
   * da IA contava "N sem responsavel" e nao havia por onde atribuir. `null`
   * tira o responsavel, e e o mesmo caminho — nao uma rota de remover.
   */
  atribuir: (id: string, responsavelId: string | null) =>
    enviar<{ atribuida: boolean }>(`/operacao/divergencias/${id}/responsavel`, { responsavelId }),

  tolerancia: () => pedir<{ toleranciaH: number }>('/operacao/conciliacao/configuracao'),
  gravarTolerancia: (toleranciaH: number, motivo: string) =>
    pedir<{ toleranciaH: number }>('/operacao/conciliacao/configuracao', {
      method: 'PUT',
      body: JSON.stringify({ toleranciaH, motivo }),
    }),
};
